import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiBarChart2, FiCheckCircle, FiClock, FiAlertCircle,
  FiInbox, FiDownload, FiMapPin,
} from 'react-icons/fi';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { supabase } from '../../lib/supabase';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import {
  zeladoriaPortalService, TIPO_ZELADORIA_LABEL, TIPO_ZELADORIA_COR,
  type IndicadoresZeladoria, type TipoZeladoria,
} from '../../services/zeladoria.service';

export function ZeladoriaIndicadoresPage() {
  const anoAtual = new Date().getFullYear();
  const [ano, setAno] = useState(anoAtual);
  const [estab, setEstab] = useState<{ nome: string; cor_primaria: string | null } | null>(null);
  const [dados, setDados] = useState<IndicadoresZeladoria | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    setCarregando(true);
    const id = getEstabelecimentoAtualId();
    Promise.all([
      zeladoriaPortalService.indicadores(id, ano),
      estab ? Promise.resolve({ data: estab, error: null }) : supabase.from('estabelecimentos').select('nome, cor_primaria').eq('id', id).maybeSingle(),
    ])
      .then(([d, e]) => {
        setDados(d);
        if (e.data) setEstab(e.data as { nome: string; cor_primaria: string | null });
      })
      .catch((e) => setErro(e instanceof Error ? e.message : 'Erro'))
      .finally(() => setCarregando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ano]);

  function exportarCSV() {
    if (!dados) return;
    const linhas = [
      ['Indicador', 'Valor'],
      ['Ano de referência', dados.ano.toString()],
      ['Total de demandas', dados.total.toString()],
      ['Resolvidas', dados.resolvidas.toString()],
      ['Aguardando atendimento', dados.abertas.toString()],
      ['Em atendimento', dados.em_atendimento.toString()],
      ['Taxa de resolução (%)', dados.taxa_resolucao_percent.toString()],
      ['Tempo médio de atendimento (horas)', dados.tempo_medio_horas.toString()],
    ];
    Object.entries(dados.distribuicao_por_tipo).forEach(([tipo, qtd]) => {
      linhas.push([`Distribuição: ${TIPO_ZELADORIA_LABEL[tipo as TipoZeladoria] ?? tipo}`, qtd.toString()]);
    });
    dados.top_bairros.forEach((b, i) => {
      linhas.push([`Top bairro #${i + 1}: ${b.bairro}`, b.qtd.toString()]);
    });
    const csv = linhas.map((l) => l.map((c) => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zeladoria_${dados.ano}_${(estab?.nome ?? 'prefeitura').toLowerCase().replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportarJSON() {
    if (!dados) return;
    const blob = new Blob([JSON.stringify(dados, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zeladoria_${dados.ano}_${(estab?.nome ?? 'prefeitura').toLowerCase().replace(/\s+/g, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const opcoesAno = [anoAtual, anoAtual - 1, anoAtual - 2, anoAtual - 3, anoAtual - 4];
  const distribuicao = dados ? Object.entries(dados.distribuicao_por_tipo).sort((a, b) => b[1] - a[1]) : [];
  const totalDistribuicao = distribuicao.reduce((s, [, q]) => s + q, 0);

  return (
    <PortalPage
      titulo="Transparência da zeladoria"
      subtitulo={`${estab?.nome ? estab.nome + ' · ' : ''}Demandas urbanas dos cidadãos`}
      icone={<FiBarChart2 size={26} />}
      voltarHref="/"
      largura="larga"
      acoes={
        <select
          value={ano}
          onChange={(e) => setAno(parseInt(e.target.value, 10))}
          className="h-9 rounded-md border border-white/20 bg-white/10 px-3 text-sm font-medium text-white focus:outline-none [&>option]:text-slate-900"
        >
          {opcoesAno.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      }
    >
      {dados && dados.total > 0 && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={exportarCSV}>
            <FiDownload size={14} /> CSV
          </Button>
          <Button variant="outline" onClick={exportarJSON}>
            <FiDownload size={14} /> JSON
          </Button>
        </div>
      )}

      {carregando ? (
        <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
      ) : erro ? (
        <Alert variante="erro">{erro}</Alert>
      ) : !dados || dados.total === 0 ? (
        <SectionCard className="mt-5">
          <div className="text-center py-12">
            <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
              <FiInbox className="text-slate-400" size={26} />
            </div>
            <p className="text-sm font-medium text-slate-700">Nenhuma demanda registrada em {ano}.</p>
          </div>
        </SectionCard>
      ) : (
        <div className="space-y-5 mt-5">
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KPI cor="slate" icone={<FiBarChart2 size={16} />} label="Demandas no ano" valor={dados.total.toString()} />
            <KPI cor="amber" icone={<FiAlertCircle size={16} />} label="Aguardando" valor={dados.abertas.toString()} />
            <KPI cor="blue" icone={<FiClock size={16} />} label="Em atendimento" valor={dados.em_atendimento.toString()} />
            <KPI cor="emerald" icone={<FiCheckCircle size={16} />} label="Resolvidas" valor={dados.resolvidas.toString()}
              hint={`${dados.taxa_resolucao_percent}% de taxa`} />
            <KPI cor="slate" icone={<FiClock size={16} />} label="Tempo médio" valor={formatarHoras(dados.tempo_medio_horas)}
              hint="da abertura à resolução" />
          </div>

          {/* Distribuicao por tipo */}
          <SectionCard titulo="Distribuição por tipo de demanda" icone={<FiBarChart2 size={18} />}>
            <div className="space-y-2">
              {distribuicao.map(([tipo, qtd]) => {
                const tipoCor = TIPO_ZELADORIA_COR[tipo as TipoZeladoria] ?? '#64748b';
                const label = TIPO_ZELADORIA_LABEL[tipo as TipoZeladoria] ?? tipo;
                const pct = totalDistribuicao > 0 ? (qtd / totalDistribuicao) * 100 : 0;
                return (
                  <div key={tipo}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full border-2 border-white shadow" style={{ background: tipoCor }} />
                        <span className="text-slate-700 font-medium">{label}</span>
                      </div>
                      <span className="text-slate-600 tabular-nums">{qtd} ({pct.toFixed(1)}%)</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded overflow-hidden">
                      <div
                        className="h-full rounded"
                        style={{ width: `${pct}%`, background: tipoCor }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>

          {/* Top bairros */}
          {dados.top_bairros.length > 0 && (
            <SectionCard titulo="Top bairros com mais demandas" icone={<FiMapPin size={18} />}>
              <ol className="space-y-2">
                {dados.top_bairros.map((b, i) => {
                  const pctTopo = dados.top_bairros[0].qtd > 0 ? (b.qtd / dados.top_bairros[0].qtd) * 100 : 0;
                  return (
                    <li key={b.bairro} className="flex items-center gap-3 text-sm">
                      <span className="text-slate-400 tabular-nums w-6">{i + 1}.</span>
                      <span className="text-slate-700 font-medium flex-1 min-w-0 truncate">{b.bairro}</span>
                      <div className="flex-1 max-w-xs h-2 bg-slate-100 rounded overflow-hidden">
                        <div className="h-full bg-institutional rounded" style={{ width: `${pctTopo}%` }} />
                      </div>
                      <span className="text-slate-600 tabular-nums w-12 text-right">{b.qtd}</span>
                    </li>
                  );
                })}
              </ol>
            </SectionCard>
          )}

          <p className="text-xs text-slate-500 text-center">
            Estes indicadores são públicos e atualizados em tempo real.
            <Link to="/zeladoria/mapa" className="ml-1 text-institutional hover:underline">Ver mapa interativo</Link>.
          </p>
        </div>
      )}
    </PortalPage>
  );
}

const COR_KPI: Record<string, string> = {
  slate: 'border-slate-200 bg-white text-slate-600',
  amber: 'border-amber-200 bg-amber-50 text-amber-800',
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  blue: 'border-blue-200 bg-blue-50 text-blue-800',
};

function KPI({ cor, icone, label, valor, hint }: { cor: string; icone: React.ReactNode; label: string; valor: string; hint?: string }) {
  return (
    <div className={`border rounded-md p-3 ${COR_KPI[cor]}`}>
      <div className="flex items-center gap-1 text-xs uppercase tracking-wider opacity-80">
        {icone} {label}
      </div>
      <div className="text-2xl font-semibold mt-1 tabular-nums">{valor}</div>
      {hint && <div className="text-xs opacity-70 mt-0.5">{hint}</div>}
    </div>
  );
}

function formatarHoras(h: number): string {
  if (!h || h < 1) return '—';
  if (h < 24) return `${Math.round(h)}h`;
  const dias = h / 24;
  if (dias < 7) return `${dias.toFixed(1)} dias`;
  return `${(dias / 7).toFixed(1)} sem`;
}
